// Budget parsing for the creator-facing campaign feed (PIT-76).
//
// `campaigns.budget` is a display string — "$1,200", "Open", occasionally
// whatever a brand typed. There is no numeric column beside it the way
// creators has `rate_num`, so filtering a feed by budget means reading the
// label back. That is exactly the kind of parsing that fails silently, so it
// lives here with tests rather than inline in the view.

/** A budget band a creator can filter the feed by. */
export interface BudgetFilter {
  label: string;
  min: number;
  max: number;
}

/**
 * Bands offered above the feed. Mirrors the RATE_FILTERS vocabulary Discover
 * already uses for creator rates, so the two sides of the marketplace filter
 * money the same way rather than inventing separate scales.
 */
export const BUDGET_FILTERS: readonly BudgetFilter[] = [
  { label: 'Any Budget', min: 0, max: Infinity },
  { label: 'Under $1K', min: 0, max: 999 },
  { label: '$1K – $2K', min: 1000, max: 2000 },
  { label: '$2K+', min: 2001, max: Infinity },
];

/**
 * Read a budget label back as whole dollars, or null when it carries no
 * number — "Open" is a real and common value, not a parse failure.
 */
export function parseBudget(budget: string | null | undefined): number | null {
  if (!budget) return null;
  const digits = budget.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d+)?$/.test(digits)) return null;
  const value = Number(digits);
  return Number.isFinite(value) ? Math.round(value) : null;
}

/**
 * Does this campaign belong in the selected band?
 *
 * A campaign whose budget carries no number always passes. "Open" means the
 * brand has not fixed a figure, and hiding open work from a creator filtering
 * by price would drop the most negotiable jobs on the board — the opposite of
 * what the filter is for.
 */
export function matchesBudget(budget: string | null | undefined, filter: BudgetFilter): boolean {
  const amount = parseBudget(budget);
  if (amount === null) return true;
  return amount >= filter.min && amount <= filter.max;
}
